import { ListenButton, Button, Pagination } from '@/components/atoms/index';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { studentWordbookApi } from '@/services/api';
import { getSlugPrefix } from '@/utils/slug';

// 단어 데이터 타입
interface WordData {
  id: number;
  english: string;
  korean: string;
  audioUrl?: string | null;
}

// 실제 데이터 상태
const initialWords: WordData[] = [];

// 단어장 리스트
export default function StudentVocabularyBook() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const slugPrefix = getSlugPrefix(location.pathname);

  const [title, setTitle] = useState('단어장');
  const [wordList, setWordList] = useState<WordData[]>(initialWords);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9; // [QA Fix] 한 페이지 최대 노출 개수 9개

  const wordbookId = Number(searchParams.get('wordbookId') || 0);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!wordbookId) {
        console.warn('wordbookId 가 없습니다.');
        setWordList([]);
        return;
      }
      try {
        setLoading(true);
        // @ts-ignore
        const res = await studentWordbookApi.detail(wordbookId);
        const apiData = res?.data || res;
        setTitle(apiData?.title || '단어장');
        const words = Array.isArray(apiData?.words) ? apiData.words : [];
        setWordList(
          words.map((w: any, idx: number) => ({
            id: w.id ?? idx,
            english: w.word ?? '',
            korean: w.meaning ?? '',
            audioUrl: w.audioUrl ?? null,
          }))
        );
        setCurrentPage(1);
      } catch (e) {
        console.error('단어장 상세 API 호출 실패:', e);
        setWordList([]);
        setCurrentPage(1);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordbookId]);

  const handleStartLearning = () => {
    navigate(`${slugPrefix}/vocabulary/detail?wordbookId=${wordbookId}`);
  };

  const totalWords = wordList.length;
  const totalPages = Math.max(1, Math.ceil(totalWords / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const pageWords = wordList.slice(startIndex, startIndex + pageSize);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main className='flex flex-1 justify-center w-full bg-white lg:my-10 card md:py-5 md:bg-transparent'>
        <div className='flex flex-col px-5 w-full'>
            <div className='flex justify-between items-center pt-6 pb-3'>
                <h1 className='heading1'>{title}</h1>
                <Button  onClick={handleStartLearning}>학습 시작하기</Button>
            </div>

            {loading ? (
              <div className='flex justify-center items-center py-12 text-gray-500'>불러오는 중...</div>
            ) : (
            <>
            <ul className='grid grid-cols-1 gap-4 pt-5 md:grid-cols-2 lg:grid-cols-3'>
                {pageWords.length === 0 ? (
                  <li className='flex flex-col items-center justify-center gap-2 py-12 text-gray-500 border border-dashed border-gray-200 rounded-lg'>
                    단어장이 비어 있습니다.
                  </li>
                ) : (
                pageWords.map((word) => (
                    <li 
                        key={word.id} 
                        className='flex relative flex-col gap-2 p-5 card !rounded-lg !shadow-[2px_2px_16px_0_rgba(0,0,0,0.08)] cursor-pointer hover:bg-gray-50 transition-colors'
                    >
                        <h1 className='pr-14 text-2xl font-bold'>{word.english}</h1>
                        <p className='text-gray-500'>{word.korean}</p>
                        <ListenButton className='absolute top-5 right-5' size={40} audioSrc={word.audioUrl || undefined} />
                    </li>
                )))}
            </ul>
            {totalWords > pageSize && (
              <div className='flex justify-center py-8'>
                <Pagination
                  total={totalWords}
                  pageSize={pageSize}
                  current={currentPage}
                  onChange={handlePageChange}
                />
              </div>
            )}
            </>
            )}
            </div>
    </main>
  )
}